import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton } from "sipli-fleet-core/components";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import { useDebounce } from "src/hooks/useDebounce";
import { useQueryGetClientLookup } from "src/queries/lookup.queries";
import { useQueryGetUsersList } from "src/queries/users.queries";
import type { LookupItem } from "src/services/lookup/lookupService";
import type { User } from "src/services/userService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import { USERS_PAGINATION, USERS_SORTING } from "./constant";
import AddEditUserModal from "./AddEditUserModal/AddEditUserModal";
import UsersTable from "./UsersTable/UsersTable";
import "./Users.css";

interface UsersSorting {
  sortBy: string | undefined;
  sortDir: "ASC" | "DESC" | undefined;
}

const getOptionalDistributorId = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const getLookupItems = (payload: unknown): LookupItem[] => {
  if (Array.isArray(payload)) return payload;

  if (!payload || typeof payload !== "object") return [];

  const lookupPayload = payload as {
    response?: unknown;
    data?: unknown;
    items?: unknown;
  };

  if (Array.isArray(lookupPayload.response)) return lookupPayload.response;
  if (Array.isArray(lookupPayload.data)) return lookupPayload.data;
  if (Array.isArray(lookupPayload.items)) return lookupPayload.items;

  return [];
};

const Users = () => {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState(USERS_PAGINATION);
  const [sorting, setSorting] = useState<UsersSorting>(USERS_SORTING);
  const [search, setSearch] = useState("");
  const [selectedClientId, setSelectedClientId] = useState<string | number | undefined>(undefined);
  const [userModal, setUserModal] = useState<{
    open: boolean;
    record: User | null;
  }>({ open: false, record: null });
  const debouncedSearch = useDebounce(search, 500);
  const { selectedDistributor } = useSelectDistributor();
  const distributorId = getOptionalDistributorId(selectedDistributor);

  const queryParams = useMemo(
    () => ({
      size: pagination.size,
      page: pagination.page,
      sortBy: sorting.sortBy ?? "ID",
      sort: sorting.sortDir ?? "DESC",
      status: "ALL",
      ...(debouncedSearch.trim() ? { query: debouncedSearch.trim() } : {}),
      ...(selectedClientId ? { clientId: selectedClientId } : {}),
    }),
    [
      debouncedSearch,
      pagination.page,
      pagination.size,
      selectedClientId,
      sorting.sortBy,
      sorting.sortDir,
    ]
  );

  const clientLookupParams = useMemo(
    () => ({
      ...(distributorId ? { distributorId } : {}),
    }),
    [distributorId]
  );

  const { data, isLoading, refetch } = useQueryGetUsersList(queryParams, {
    enabled: true,
  });
  const { data: clientLookupData, isLoading: isClientLookupLoading } = useQueryGetClientLookup(
    clientLookupParams,
    { enabled: true }
  );
  const response = data?.data?.response;
  const users: User[] = response?.users ?? [];
  const clientLookupPayload = (clientLookupData as { data?: unknown } | undefined)?.data;
  const clientOptions = useMemo(
    () =>
      getLookupItems(clientLookupPayload)
        .map((client) => ({
          label: String(client.companyName ?? client.name ?? client.label ?? "--") || "--",
          value: client.id ?? client.value,
        }))
        .filter(
          (option): option is { label: string; value: string | number } =>
            option.value !== undefined && option.value !== null && option.value !== ""
        ),
    [clientLookupPayload]
  );

  useEffect(() => {
    setSelectedClientId(undefined);
    setPagination((previous) => ({ ...previous, page: 0 }));
  }, [distributorId]);

  return (
    <section className="pb-8 pl-2 bg-gray1">
      <CustomSharedTitleSection
        titleKey="users"
        showBtn={false}
        rightBtnComponent={
          <CustomButton
            type="primary"
            buttonSize="medium"
            className="!w-[94px] !h-[40px] !text-gray2"
            onClick={() => setUserModal({ open: true, record: null })}
            label={t("create")}
          />
        }
        titleClassName="!text-[25.23px] !font-[700]"
      />

      <div className="flex flex-col overflow-hidden bg-white rounded-[16px]">
        <UsersTable
          data={users}
          isLoading={isLoading}
          pagination={{
            ...pagination,
            total: response?.totalItems ?? users.length,
          }}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          search={search}
          setSearch={setSearch}
          selectedClientId={selectedClientId}
          setSelectedClientId={setSelectedClientId}
          clientOptions={clientOptions}
          isClientLookupLoading={isClientLookupLoading}
          refetchList={refetch}
          onEditUser={(record) => {
            setUserModal({ open: true, record });
          }}
        />
      </div>

      <AddEditUserModal
        open={userModal.open}
        record={userModal.record}
        onClose={() => {
          setUserModal({ open: false, record: null });
          void refetch();
        }}
      />
    </section>
  );
};

export default Users;
